import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtMT } from "@/lib/mountainTime";
import { smsSession, smsSessionStart, smsSessionStop, errText } from "@/lib/bidCenterApi";

const EV = { new_high_bid: "New valid high bid", auction_extended: "Auction extended", completed: "Auction completed" };
const END = { stopped_by_admin: "stopped by admin", auction_completed: "auction completed", expired: "expired after 24 h" };
const DEL = { sent: "Accepted by Twilio", delivered: "Delivered to phone", undelivered: "Not delivered", failed: "Failed", sending: "Sending…" };

function SessionLog({ rows }) {
  if (!rows.length) return <p className="text-xs text-stone-500" data-testid="sms-session-log-empty">No texts sent in this session yet.</p>;
  return (
    <ul data-testid="sms-session-log" className="text-xs divide-y border rounded-md bg-white">{rows.map((d) => (
      <li key={d.id} data-testid={`sms-session-row-${d.id}`} className="p-1.5">
        {fmtMT(d.created_at)} · {EV[d.event] || d.event} · {d.recipient_name} · <b>{DEL[d.provider_status] || DEL[d.status] || d.status}</b>{d.error ? ` · ${d.error}` : ""}
      </li>))}</ul>
  );
}

export function SmsTestSession() {
  const [v, setV] = useState(null);
  const [horse, setHorse] = useState("");
  const [pick, setPick] = useState([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { smsSession().then(setV).catch(() => {}); }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  if (!v || !v.preview) return null;
  const s = v.session;
  const toggle = (id) => setPick((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length < v.max_recipients ? [...p, id] : p));
  const run = async (fn, msg) => { setBusy(true); try { setV(await fn()); toast.success(msg); setCode(""); } catch (e) { toast.error(errText(e)); } setBusy(false); };
  const start = () => run(() => smsSessionStart({ horse_id: horse, recipient_ids: pick, confirm: code }), "Controlled test started");
  const stop = () => window.confirm("Stop all real test texts now?") && run(smsSessionStop, "Controlled test stopped");
  return (
    <div data-testid="sms-test-session" className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] p-3 space-y-2 text-sm">
      <p className="font-semibold">Controlled Test Auction (Preview only)</p>
      <p className="text-xs text-[#92400E]">Real texts for ONE horse only: {v.events.map((e) => EV[e]).join(", ")}. Up to {v.max_recipients} consented phones. Ends when the auction completes, after 24 hours, or when you stop it. Every other horse and production stay blocked.</p>
      {v.active ? <>
        <p data-testid="sms-session-active" className="text-xs text-emerald-800"><b>ON</b> · {s.horse_name} · to {s.recipient_names.join(" and ")} · started {fmtMT(s.started_at)} by {s.by} · ends by {fmtMT(s.expires_at)}</p>
        <Button data-testid="sms-session-stop" variant="outline" disabled={busy} onClick={stop} className="h-10 sm:h-9 border-red-300 text-red-700">Stop real test texts</Button>
      </> : <>
        {s && <p data-testid="sms-session-ended" className="text-xs text-stone-600">Last session ({s.horse_name}) ended {s.ended_at ? fmtMT(s.ended_at) : ""} · {END[s.end_reason] || s.end_reason}</p>}
        <select data-testid="sms-session-horse" value={horse} onChange={(e) => setHorse(e.target.value)} className="h-11 sm:h-9 w-full rounded-md border px-2 bg-white text-sm">
          <option value="">Choose the test horse…</option>{v.horses.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
        <div className="space-y-1">{v.recipients.map((r) => (
          <label key={r.id} className="flex items-center gap-2 text-xs"><Checkbox data-testid={`sms-session-rcpt-${r.id}`} checked={pick.includes(r.id)} onCheckedChange={() => toggle(r.id)} />{r.name} · {r.phone_hint}</label>))}
          {!v.recipients.length && <p className="text-xs text-[#92400E]">Add active recipients with consent in SMS Notifications first.</p>}</div>
        <div className="flex gap-2">
          <Input data-testid="sms-session-confirm" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Type START TEST" className="h-10 sm:h-9" />
          <Button data-testid="sms-session-start" disabled={busy || !horse || !pick.length || code.trim().toUpperCase() !== "START TEST"} onClick={start} className="h-10 sm:h-9 shrink-0 bg-stone-800 text-white">Start</Button>
        </div>
      </>}
      {s && <SessionLog rows={v.deliveries} />}
    </div>
  );
}
